import { useState, useEffect, useRef, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Icon } from "@ugitgud/legos/ui/icon/Icon";
import { SvgNavTierlist } from "@ugitgud/legos/ui/icon/SVG/NavTierlist";
import { SvgNavChampions } from "@ugitgud/legos/ui/icon/SVG/NavChampions";
import { RiotLogo } from "@ugitgud/legos/components/home-page/icons/RiotLogo";
import { AppRoutes } from "@ugg/shared/routes/app-routes";

interface ExclusiveFeatureView {
  title: string;
  description: string;
  image: string;
  icon: ReactNode;
}

interface ExclusiveFeatureCarouselProps {
  featureListRef?: React.RefObject<HTMLDivElement>;
  featuresHref?: string;
}

const VIEWS: ExclusiveFeatureView[] = [
  {
    title: "Champion Stats",
    description: "Curated insights & Stats based on your data directly on the champion build page",
    image: "https://static.bigbrain.gg/assets/ugg/icons/plus-champion-stats.webp",
    icon: <SvgNavTierlist />,
  },
  {
    title: "Personal Counters",
    description: "Per-matchup counters and stats based on your champion and past games",
    image: "https://static.bigbrain.gg/assets/ugg/icons/plus-counters.webp",
    icon: <SvgNavChampions />,
  },
  {
    title: "Personal Tier List",
    description: "A dedicated tier list page on your profile displaying your best champions.",
    image: "https://static.bigbrain.gg/assets/ugg/icons/plus-tier-list.webp",
    icon: <SvgNavTierlist />,
  },
];

const ACTIVE_DOT_BOX_SHADOW = "0 0 6.7px 0 rgba(244, 200, 116, 0.75), 0 0 14.9px 0 rgba(244, 200, 116, 0.50)";

const AUTO_SCROLL_INTERVAL = 6000;

export const ExclusiveFeatureCarousel = ({ featureListRef, featuresHref }: ExclusiveFeatureCarouselProps) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const activeView = VIEWS[activeIndex];
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setActiveIndex((i) => (i + 1) % VIEWS.length);
    }, AUTO_SCROLL_INTERVAL);
  };

  useEffect(() => {
    startTimer();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const handleDotClick = (index: number) => {
    setActiveIndex(index);
    startTimer();
  };

  const handleSeeAllFeatures = () => {
    featureListRef?.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section className="max-w-[1016px] w-full mx-auto px-3 lg:px-0 mb-[40px] md:mb-[120px]">
      <div className="flex flex-col md:flex-row items-center flex-nowrap gap-4 md:gap-[46px] justify-between">
        <div className="flex md:hidden items-center gap-[11.84px] w-full max-w-[602px] order-1 mb-2">
          <span className="rounded-[3px] text-[14.422px] font-semibold border border-accent-yellow-400 px-[4.36px] py-[3.4px] bg-accent-brown-400 text-accent-yellow-400">
            PLUS
          </span>
          <p className="text-[24px] font-bold text-white">Exclusive Feature</p>
        </div>

        <div className="flex flex-col shrink-0 w-full max-w-[602px] md:max-w-[366px] order-3 md:order-1">
          <div className="hidden md:flex items-center gap-[11.84px] mb-9">
            <span className="rounded-[3px] text-[14.422px] font-semibold border border-accent-yellow-400 px-[4.36px] py-[3.4px] bg-accent-brown-400 text-accent-yellow-400">
              PLUS
            </span>
            <p className="text-[24px] font-bold text-white">Exclusive Feature</p>
          </div>
          <div className="flex items-center gap-3 mb-3">
            <div className="flex items-center justify-center bg-purple-400 rounded-[3px] w-[26px] h-[26px]">
              <Icon icon={activeView.icon} className="w-[15px] h-auto text-accent-yellow-400" />
            </div>
            <h3 className="text-[36px] font-bold text-white leading-tight">{activeView.title}</h3>
          </div>
          <p className="text-lavender-50 text-[18px] font-normal mb-6 leading-snug">{activeView.description}</p>
          <div className="flex items-center gap-5 mb-4.5">
            <Link
              to={`${AppRoutes.CHECKOUT}?plan=12-month`}
              className="group relative flex items-center justify-center rounded-[3px] h-9 w-[150px] bg-accent-brown-400 border border-accent-yellow-400 text-white text-[14px] font-semibold transition-all"
            >
              <span
                className="absolute inset-0 rounded-[3px] opacity-0 group-hover:opacity-100 transition-opacity"
                style={{
                  background: "linear-gradient(0deg, rgba(244, 200, 116, 0.05) 0%, rgba(244, 200, 116, 0.05) 100%), #25254B",
                }}
              />
              <span className="relative">
                Try for <span className="text-accent-yellow-400 font-bold">Free</span>
              </span>
            </Link>
            {featuresHref ? (
              <Link
                to={featuresHref}
                className="flex text-[14px] font-semibold items-center justify-center rounded-[3px] h-9 w-[150px] text-white bg-lavender-500"
              >
                See all features
              </Link>
            ) : (
              <button
                type="button"
                onClick={handleSeeAllFeatures}
                className="text-[14px] font-semibold items-center justify-center rounded-[3px] h-9 w-[150px] text-white bg-lavender-500"
              >
                See all features
              </button>
            )}
          </div>
          <div className="flex items-center gap-2 text-[12px] font-normal text-accent-gray-200">
            <RiotLogo className="w-6 h-6 fill-accent-gray-200" /> Fully compliant with Riot Games
          </div>
        </div>

        <div className="w-full md:w-auto max-w-[602px] order-2 md:order-2">
          <img src={activeView.image} alt={activeView.title} className="w-full md:w-[602px] h-auto md:h-[339px] object-cover" />
        </div>
      </div>

      {/* Pagination dots */}
      <div className="flex items-center justify-center gap-3 mt-6">
        {VIEWS.map((_, index) => {
          const isActive = index === activeIndex;
          return (
            <button
              key={index}
              type="button"
              aria-label={`Show feature ${index + 1}`}
              aria-current={isActive ? "true" : undefined}
              onClick={() => handleDotClick(index)}
              className={
                isActive
                  ? "w-3 h-3 rounded-[12px] bg-accent-yellow-400"
                  : "w-3 h-3 rounded-[12px] bg-purple-100 transition-opacity hover:opacity-80"
              }
              style={isActive ? { boxShadow: ACTIVE_DOT_BOX_SHADOW } : undefined}
            />
          );
        })}
      </div>
    </section>
  );
};
